import { makeAutoObservable } from "mobx";
import type { RootStore, ServiceStore } from "@intrasync/stores";
import { Job, CostAllocation, JobStatus, CostCategory, JobBudget } from "../types";

export interface JobFormData {
  id?: string;
  jobNumber: string;
  jobName: string;
  customerId?: string;
  customerName?: string;
  productionOrderId?: string;
  status: JobStatus;
  startDate?: string;
  completedDate?: string;
  estimatedCost: number;
  actualCost: number;
  budgetedHours: number;
  actualHours: number;
  description?: string;
}

export interface CostAllocationFormData {
  id?: string;
  jobId: string;
  category: CostCategory;
  description: string;
  quantity: number;
  unitCost: number;
  totalCost: number;
  allocationDate: string;
  referenceId?: string;
}

class JobCostingStore {
  rootStore: RootStore;

  // Observables
  selectedJobId: string | null = null;
  formData: JobFormData;
  costAllocationFormData: CostAllocationFormData;
  isEditMode = false;
  filterStatus: "all" | JobStatus = "all";
  searchQuery = "";
  dateRange = {
    start: "",
    end: "",
  };

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    this.formData = this.getInitialFormData();
    this.costAllocationFormData = this.getInitialCostAllocationFormData();

    makeAutoObservable(this, {
      rootStore: false,
    });
  }

  // Actions
  setFilterStatus = (status: "all" | JobStatus) => {
    this.filterStatus = status;
  };

  setSearchQuery = (query: string) => {
    this.searchQuery = query;
  };

  setDateRange = (start: string, end: string) => {
    this.dateRange = { start, end };
  };

  selectJob = (jobId: string | null) => {
    this.selectedJobId = jobId;
  };

  openCreateJobForm = () => {
    this.formData = this.getInitialFormData();
    this.isEditMode = false;
    this.selectedJobId = null;
  };

  openEditJobForm = (jobId: string) => {
    const job = this.jobs.find((j) => j.id === jobId);
    if (!job) return;

    this.formData = {
      id: job.id,
      jobNumber: job.jobNumber,
      jobName: job.jobName,
      customerId: job.customerId,
      customerName: job.customerName,
      productionOrderId: job.productionOrderId,
      status: job.status,
      startDate: job.startDate,
      completedDate: job.completedDate,
      estimatedCost: job.estimatedCost,
      actualCost: job.actualCost,
      budgetedHours: job.budgetedHours,
      actualHours: job.actualHours,
      description: job.description,
    };
    this.isEditMode = true;
    this.selectedJobId = jobId;
  };

  closeJobForm = () => {
    this.isEditMode = false;
    this.selectedJobId = null;
    this.formData = this.getInitialFormData();
  };

  updateFormData = (field: keyof JobFormData, value: any) => {
    this.formData = { ...this.formData, [field]: value };
  };

  updateCostAllocationFormData = (
    field: keyof CostAllocationFormData,
    value: any
  ) => {
    // Auto-calculate total cost when quantity or unit cost changes
    if (field === "quantity" || field === "unitCost") {
      const quantity =
        field === "quantity" ? value : this.costAllocationFormData.quantity;
      const unitCost =
        field === "unitCost" ? value : this.costAllocationFormData.unitCost;
      const totalCost = quantity * unitCost;

      this.costAllocationFormData = {
        ...this.costAllocationFormData,
        [field]: value,
        totalCost,
      };
    } else {
      this.costAllocationFormData = {
        ...this.costAllocationFormData,
        [field]: value,
      };
    }
  };

  openAllocateCostForm = (jobId: string) => {
    this.costAllocationFormData = {
      ...this.getInitialCostAllocationFormData(),
      jobId,
    };
  };

  saveJob = async () => {
    try {
      if (this.isEditMode && this.formData.id) {
        await this.jobStore.patch(
          Number(this.formData.id),
          this.formData as unknown as Parameters<typeof this.jobStore.patch>[1]
        );
      } else {
        await this.jobStore.create(
          this.formData as unknown as Parameters<typeof this.jobStore.create>[0]
        );
      }
      this.closeJobForm();
    } catch (error) {
      console.error("Failed to save job:", error);
      throw error;
    }
  };

  saveCostAllocation = async () => {
    try {
      await this.costAllocationsStore.create(
        this.costAllocationFormData as unknown as Parameters<
          typeof this.costAllocationsStore.create
        >[0]
      );

      // Update job's actual cost
      const job = this.jobs.find((j) => j.id === this.costAllocationFormData.jobId);
      if (job) {
        const newActualCost = job.actualCost + this.costAllocationFormData.totalCost;
        await this.jobStore.patch(Number(job.id), { actualCost: newActualCost });
      }

      this.costAllocationFormData = this.getInitialCostAllocationFormData();
    } catch (error) {
      console.error("Failed to save cost allocation:", error);
      throw error;
    }
  };

  updateJobStatus = async (jobId: string, status: JobStatus) => {
    try {
      const updates: any = { status };

      // Auto-set dates based on status
      if (status === JobStatus.IN_PROGRESS && !this.getJob(jobId)?.startDate) {
        updates.startDate = new Date().toISOString().split("T")[0];
      }
      if (status === JobStatus.COMPLETED) {
        updates.completedDate = new Date().toISOString().split("T")[0];
      }

      await this.jobStore.patch(Number(jobId), updates);
    } catch (error) {
      console.error("Failed to update job status:", error);
      throw error;
    }
  };

  deleteJob = async (jobId: string) => {
    try {
      const job = this.jobs.find((j) => j.id === jobId);
      if (job && job.actualCost > 0) {
        throw new Error(
          "Cannot delete job with cost allocations. Close the job instead."
        );
      }
      await this.jobStore.remove(Number(jobId));
    } catch (error) {
      console.error("Failed to delete job:", error);
      throw error;
    }
  };

  loadJobs = async () => {
    try {
      await this.jobStore.find({});
    } catch (error) {
      console.error("Failed to load jobs:", error);
      throw error;
    }
  };

  loadCostAllocations = async () => {
    try {
      await this.costAllocationsStore.find({});
    } catch (error) {
      console.error("Failed to load cost allocations:", error);
      throw error;
    }
  };

  private getInitialFormData(): JobFormData {
    return {
      jobNumber: `JOB-${Date.now()}`,
      jobName: "",
      status: JobStatus.NOT_STARTED,
      estimatedCost: 0,
      actualCost: 0,
      budgetedHours: 0,
      actualHours: 0,
    };
  }

  private getInitialCostAllocationFormData(): CostAllocationFormData {
    return {
      jobId: "",
      category: CostCategory.MATERIAL,
      description: "",
      quantity: 0,
      unitCost: 0,
      totalCost: 0,
      allocationDate: new Date().toISOString().split("T")[0],
    };
  }

  // Computed values
  get jobStore() {
    return this.rootStore.jobStore as ServiceStore;
  }

  get costAllocationsStore() {
    return this.rootStore.costAllocationsStore as ServiceStore;
  }

  get jobs(): Job[] {
    return (this.jobStore.entities as unknown as Job[]) || [];
  }

  get costAllocations(): CostAllocation[] {
    return (
      (this.costAllocationsStore.entities as unknown as CostAllocation[]) || []
    );
  }

  get selectedJob(): Job | null {
    return this.selectedJobId
      ? (this.jobs.find((j) => j.id === this.selectedJobId) || null)
      : null;
  }

  getJob = (jobId: string): Job | undefined => {
    return this.jobs.find((j) => j.id === jobId);
  };

  get isLoading(): boolean {
    return this.jobStore.isServicePending || this.costAllocationsStore.isServicePending;
  }

  get filteredJobs(): Job[] {
    let filtered = this.jobs;

    // Filter by status
    if (this.filterStatus !== "all") {
      filtered = filtered.filter((job) => job.status === this.filterStatus);
    }

    // Filter by search query
    if (this.searchQuery) {
      const query = this.searchQuery.toLowerCase();
      filtered = filtered.filter(
        (job) =>
          job.jobNumber.toLowerCase().includes(query) ||
          job.jobName.toLowerCase().includes(query) ||
          job.customerName?.toLowerCase().includes(query)
      );
    }

    // Filter by date range
    if (this.dateRange.start) {
      filtered = filtered.filter(
        (job) => job.startDate && job.startDate >= this.dateRange.start
      );
    }

    if (this.dateRange.end) {
      filtered = filtered.filter(
        (job) => job.startDate && job.startDate <= this.dateRange.end
      );
    }

    return filtered;
  }

  // Get cost allocations for a specific job
  getJobCostAllocations = (jobId: string): CostAllocation[] => {
    return this.costAllocations.filter((ca) => ca.jobId === jobId);
  };

  // Calculate job budget by category
  getJobBudget = (jobId: string): JobBudget[] => {
    const job = this.getJob(jobId);
    if (!job) return [];

    const allocations = this.getJobCostAllocations(jobId);
    const categories = Object.values(CostCategory);

    return categories.map((category) => {
      const actualAmount = allocations
        .filter((a) => a.category === category)
        .reduce((sum, a) => sum + a.totalCost, 0);

      // For budget, divide estimated cost equally (simplified)
      const budgetedAmount = job.estimatedCost / categories.length;
      const variance = actualAmount - budgetedAmount;
      const variancePercent =
        budgetedAmount > 0 ? (variance / budgetedAmount) * 100 : 0;

      return {
        id: `${jobId}-${category}`,
        jobId,
        category,
        budgetedAmount,
        actualAmount,
        variance,
        variancePercent,
      };
    });
  };

  // Job Summary Statistics
  get jobSummary() {
    const notStarted = this.jobs.filter(
      (j) => j.status === JobStatus.NOT_STARTED
    ).length;
    const inProgress = this.jobs.filter(
      (j) => j.status === JobStatus.IN_PROGRESS
    ).length;
    const completed = this.jobs.filter(
      (j) => j.status === JobStatus.COMPLETED
    ).length;
    const closed = this.jobs.filter((j) => j.status === JobStatus.CLOSED).length;

    const totalEstimated = this.jobs.reduce((sum, j) => sum + j.estimatedCost, 0);
    const totalActual = this.jobs.reduce((sum, j) => sum + j.actualCost, 0);
    const totalVariance = totalActual - totalEstimated;

    return {
      notStarted,
      inProgress,
      completed,
      closed,
      totalJobs: this.jobs.length,
      totalEstimated,
      totalActual,
      totalVariance,
      variancePercent:
        totalEstimated > 0 ? (totalVariance / totalEstimated) * 100 : 0,
    };
  }

  // Cost breakdown by category across all jobs
  get costBreakdown() {
    const categories = Object.values(CostCategory);
    return categories.map((category) => {
      const total = this.costAllocations
        .filter((ca) => ca.category === category)
        .reduce((sum, ca) => sum + ca.totalCost, 0);

      return {
        category,
        total,
        percentage:
          this.jobSummary.totalActual > 0
            ? (total / this.jobSummary.totalActual) * 100
            : 0,
      };
    });
  }
}

export default JobCostingStore;
